/**
 * TopBar Component
 */
import Clock from '../../atoms/text/topBarClock';
import Container from '../../atoms/container/topBarContainer';
import FlavourConfig from '../../../../shared/config/flavours';
import Focus from '../../../../shared/types/enums/focus';
import Logo from '../../atoms/images/topBarLogo';
import Pages from '../../../../shared/types/enums/pages';
import Plan from '../../atoms/text/topBarPlan';
import TranslationManager from '../../../../shared/assets/locale/translationsManager';
import UserClass from '../../../../shared/classes/user';
import User from '../../atoms/text/topBarUser';
import { appSelector } from '../../../../shared/store/slices/app.slice';
import { focusSelector } from '../../../../shared/store/slices/focus.slice';
import { routerSelector } from '../../../../shared/store/slices/router.slice';
import { useAppSelector } from '../../../../shared/store/hooks';
import { useEffect, useMemo, useState } from 'react';
import { userSelector } from '../../../../shared/store/slices/user.slice';

/**
 * TopBar Component
 *
 * Displays the top bar with user greeting, clock, and logo.
 * The visibility of the top bar is conditionally controlled based on:
 * - Mini detail description
 * - Current page
 * - Current focus state
 */
export default function TopBar() {
  // Get mini detail description (used to determine if top bar should be hidden)
  const {
    status: { description },
  } = useAppSelector(appSelector);

  // Get current focus state (determines which UI element has focus)
  const { focus } = useAppSelector(focusSelector);

  // Get current user name (displayed in greeting if available)
  const { name, planId } = useAppSelector(userSelector);

  // Get current page route
  const {
    route: { Page },
  } = useAppSelector(routerSelector);

  // State to control top bar visibility
  const [display, setDisplay] = useState(false);
  /**
   * Determines when the top bar should be displayed.
   *
   * Display conditions:
   * 1. No mini detail description is present
   * 2. Current page is NOT Detail or Search page
   * 3. AND one of the following:
   *    - Focus is on specific UI elements (channel list, menu, sliders, etc.)
   *    - Focus includes RAILS_LIST
   *    - Current page is one of the specified pages (channel list, account, settings, etc.)
   */
  useEffect(() => {
    setDisplay(
      !description &&
        ![Pages.SEARCH].includes(Page) &&
        ([
          -1,
          Focus.CHANNEL_LIST,
          Focus.CHANNEL_LIST_EVENTS,
          Focus.CHANNEL_LIST_UPGRADE,
          Focus.LOGIN,
          Focus.MAIN_EMPTY,
          Focus.MENU,
          Focus.MODAL_BUTTONS,
          Focus.MOSAIC,
          Focus.SLIDERS,
        ].includes(focus as number) ||
          (Array.isArray(focus) && focus.flat().includes(Focus.RAILS_LIST)) ||
          [
            Pages.CHANNEL_LIST,
            Pages.CHANNELS_MAIN,
            Pages.ACCOUNT,
            Pages.DETAIL,
            Pages.DISK,
            Pages.SETTING_LANGUAGES,
            Pages.LOGOUT,
            Pages.MANAGE_CHANNELS,
            Pages.POWER_SAVING,
          ].includes(Page))
    );
  }, [description, focus, Page]);

  // Get plan information
  const { plan, userName } = useMemo(() => {
    return {
      plan: FlavourConfig.showPlan ? UserClass.getPlan(planId) : null,
      userName: name?.length
        ? `${TranslationManager.get('COMPONENTS.TOP_BAR.HELLO')} ${name},`
        : '',
    };
  }, [name, planId]);

  // Render the top bar
  return (
    <Container elOpacity={display} id={'topBarContainer'}>
      {/* Conditionally render user greeting if user name is available */}
      {userName ? <User>{userName}</User> : null}
      {/* Always render clock */}
      <Clock />
      {/* Always render logo */}
      <Logo margin={false} />
      {/* Conditionally render plan if available */}
      {plan ? <Plan /*color={plan?.colorHex}*/>{plan?.shortName}</Plan> : null}
    </Container>
  );
}
